import React, { useState, useRef, useCallback } from "react";
import { api } from "../api";
import Lightbox from "./Lightbox";

const EMOJI_ONLY_RE = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})+$/u;

function isEmojiOnly(text) {
  return text && EMOJI_ONLY_RE.test(text.trim());
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function MediaContent({ msg }) {
  const url = api.mediaUrl(msg.mediaUrl);
  const [lightbox, setLightbox] = useState(false);

  switch (msg.messageType) {
    case "IMAGE":
      return (
        <>
          <img
            src={url}
            alt={msg.mediaName || "immagine"}
            className="msg-image"
            onClick={() => setLightbox(true)}
          />
          {lightbox && (
            <Lightbox src={url} alt={msg.mediaName || "immagine"} onClose={() => setLightbox(false)} />
          )}
        </>
      );

    case "VIDEO":
      return (
        <video controls className="msg-video" preload="metadata">
          <source src={url} />
        </video>
      );

    case "AUDIO":
      return (
        <audio controls className="msg-audio" preload="metadata">
          <source src={url} />
        </audio>
      );

    case "DOCUMENT":
      return (
        <a href={url} download={msg.mediaName} className="msg-document" target="_blank" rel="noreferrer">
          <span className="doc-icon">📄</span>
          <span className="doc-info">
            <span className="doc-name">{msg.mediaName || "Documento"}</span>
            {msg.mediaSize && <span className="doc-size">{formatSize(msg.mediaSize)}</span>}
          </span>
          <span className="doc-download">⬇</span>
        </a>
      );

    default:
      return null;
  }
}

const LONG_PRESS_MS = 500;

export default function MessageBubble({ msg, isMine, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const longPressTimer = useRef(null);

  // ── Long press (mobile) ───────────────────────────────────────────────────
  const startPress = useCallback(() => {
    if (!isMine) return;
    longPressTimer.current = setTimeout(() => setMenuOpen(true), LONG_PRESS_MS);
  }, [isMine]);

  const cancelPress = useCallback(() => {
    clearTimeout(longPressTimer.current);
  }, []);

  // ── Click destro (desktop) ────────────────────────────────────────────────
  const handleContextMenu = useCallback((e) => {
    if (!isMine) return;
    e.preventDefault();
    setMenuOpen(true);
  }, [isMine]);

  function handleDelete() {
    setMenuOpen(false);
    onDelete(msg.id);
  }

  return (
    <div className={`bubble-wrapper ${isMine ? "mine" : "theirs"}`}>
      {/* Menu contestuale */}
      {menuOpen && (
        <div className={`bubble-menu ${isMine ? "bubble-menu-mine" : ""}`}>
          <button className="bubble-menu-item delete" onClick={handleDelete}>
            🗑 Elimina
          </button>
          <button className="bubble-menu-item" onClick={() => setMenuOpen(false)}>
            Annulla
          </button>
        </div>
      )}

      {/* Overlay per chiudere il menu toccando fuori */}
      {menuOpen && <div className="bubble-menu-backdrop" onClick={() => setMenuOpen(false)} />}

      <div
        className={`bubble ${isMine ? "bubble-out" : "bubble-in"} ${isEmojiOnly(msg.content) && msg.messageType === "TEXT" ? "bubble-transparent" : ""}`}
        onTouchStart={startPress}
        onTouchEnd={cancelPress}
        onTouchMove={cancelPress}
        onContextMenu={handleContextMenu}
      >
        {msg.messageType !== "TEXT" && <MediaContent msg={msg} />}
        {msg.content && (
          <p className={isEmojiOnly(msg.content) ? "bubble-text bubble-emoji-only" : "bubble-text"}>
            {msg.content}
          </p>
        )}
        <span className="bubble-time">
          {formatTime(msg.createdAt)}
          {isMine && (
            <span className="bubble-ticks" title={msg.readAt ? "Letto" : "Consegnato"}>
              {msg.readAt ? "✓✓" : "✓"}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
